/** Гроші в API — цілі мінорні одиниці (копійки/центи). */

const fmtCache = new Map<string, Intl.NumberFormat>();

/** Символи валют справ (050): гривня, євро, молдовський лей. Інші валюти — кодом («USD», «PLN»). */
export const CURRENCY_SYMBOLS: Record<string, string> = { UAH: '₴', EUR: '€', MDL: 'L' };

/** Символ валюти або сам код, якщо символу немає. */
export function currencySymbol(currency: string): string {
  const c = (currency || 'UAH').toUpperCase();
  return CURRENCY_SYMBOLS[c] ?? c;
}

function formatter(digits: number, locale: string, compact: boolean): Intl.NumberFormat {
  const key = `${locale}|${digits}|${compact ? 'c' : 'f'}`;
  let f = fmtCache.get(key);
  if (!f) {
    const d = compact ? 0 : digits;
    f = new Intl.NumberFormat(locale, { minimumFractionDigits: d, maximumFractionDigits: d });
    fmtCache.set(key, f);
  }
  return f;
}

/** Кількість знаків після коми для валюти (UAH/EUR — 2, JPY — 0). */
export function currencyDigits(currency: string): number {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

export function minorToMajor(minor: number, currency = 'UAH'): number {
  return minor / 10 ** currencyDigits(currency);
}

export function majorToMinor(major: number, currency = 'UAH'): number {
  return Math.round(major * 10 ** currencyDigits(currency));
}

export interface FormatMoneyOptions {
  locale?: string;
  /** Без копійок. */
  compact?: boolean;
}

export function formatMoney(
  minor: number | null | undefined,
  currency = 'UAH',
  { locale = 'uk-UA', compact = false }: FormatMoneyOptions = {},
): string {
  if (minor == null || Number.isNaN(minor)) return '—';
  const code = (currency || 'UAH').toUpperCase();
  const major = minorToMajor(minor, code);
  try {
    // число — за локаллю, позначка валюти — завжди наша (₴, €, L або код), незалежно від версії ICU
    return `${formatter(currencyDigits(code), locale, compact).format(major)}\u00a0${currencySymbol(code)}`;
  } catch {
    return `${major.toFixed(2)} ${code}`;
  }
}

/**
 * Розбирає введену користувачем суму ("1 234,50", "1234.5", "₴ 99") у мінорні одиниці.
 * Повертає null, якщо рядок порожній або некоректний.
 */
export function parseMoney(input: string, currency = 'UAH'): number | null {
  const cleaned = input.replace(/[\s\u00a0\u202f]/g, '').replace(/[^\d,.-]/g, '');
  if (!cleaned || cleaned === '-') return null;
  let normalized = cleaned;
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  if (lastComma > -1 && lastDot > -1) {
    // десятковий — той, що правіше; інший — розділювач тисяч
    const dec = lastComma > lastDot ? ',' : '.';
    const thou = dec === ',' ? '.' : ',';
    normalized = cleaned.split(thou).join('').replace(dec, '.');
  } else if (lastComma > -1) {
    normalized = cleaned.replace(/,/g, '.');
  }
  if ((normalized.match(/\./g) ?? []).length > 1) return null;
  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  return majorToMinor(n, currency);
}

/** Значення для <input> з мінорних одиниць: 123450 → "1234.50". */
export function minorToInput(minor: number | null | undefined, currency = 'UAH'): string {
  if (minor == null) return '';
  const d = currencyDigits(currency);
  return minorToMajor(minor, currency).toFixed(d);
}
